import Spacer from '@polestar/component-warehouse-react/components/spacer/Spacer';
import Orientations from '@polestar/component-warehouse-react/tokens/orientations/Orientations';
import Spacings, { createCustomSpacing, } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import React, { Children, forwardRef, Fragment, useContext } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { buttonIconOverride, formatPath, selectIconBasedOnPath } from './BulletList.helpers';
import LineHeights from '@polestar/component-warehouse-react/tokens/line-heights/LineHeights';
import { useDirection } from '@polestar/component-warehouse-react/contexts/direction';
export var BulletListVariants;
(function (BulletListVariants) {
    BulletListVariants["bullets"] = "bullets";
    BulletListVariants["numbered"] = "numbered";
})(BulletListVariants || (BulletListVariants = {}));
export var BulletListBulletIconVariants;
(function (BulletListBulletIconVariants) {
    BulletListBulletIconVariants["roundOpen"] = "roundOpen";
    BulletListBulletIconVariants["roundClosed"] = "roundClosed";
    BulletListBulletIconVariants["square"] = "square";
})(BulletListBulletIconVariants || (BulletListBulletIconVariants = {}));
const BulletListContext = React.createContext({
    level: 0,
    indexes: [],
    path: [],
    variant: BulletListVariants.bullets,
    bulletIcon: undefined,
    compact: false,
});
const BulletList = forwardRef(({ variant, children, compact = false, bulletIcon, ...props }, ref) => {
    const { level, indexes, path, variant: contextVariant, compact: contextCompact, bulletIcon: contextBulletIcon, } = useContext(BulletListContext);
    const { isRTL } = useDirection();
    // set initial context value
    const contextValue = {
        level: level + 1,
        path: [],
    };
    // use context
    if (variant === undefined && contextVariant) {
        variant = contextVariant;
    }
    else if (variant === undefined) {
        variant = BulletListVariants.bullets;
    }
    if (compact === undefined && contextCompact) {
        compact = contextCompact;
    }
    else if (compact === undefined) {
        compact = false;
    }
    if (bulletIcon === undefined && contextBulletIcon) {
        bulletIcon = contextBulletIcon;
    }
    if (level > 2) {
        // eslint-disable-next-line no-console
        console.warn('[BulletList] The maximum amount of levels inside a BulletList is 3. Please remove the 4th(+) levels and re-organise your content to follow the Polestar Design Guidelines.');
    }
    return (React.createElement(Container, { ...props, ref: ref }, Children.map(children, (child, index) => {
        const icon = bulletIcon !== undefined
            ? buttonIconOverride(bulletIcon)
            : selectIconBasedOnPath(level);
        const currentPath = [...path, index];
        return (React.createElement(BulletListContext.Provider, { value: {
                ...contextValue,
                indexes: indexes,
                path: path ? [...path, index] : [index],
                variant,
                bulletIcon,
                compact,
            } },
            React.createElement(Fragment, null,
                React.createElement(ListItem, { addSpace: !(level === 0 && index === 0), compact: !!compact },
                    variant === BulletListVariants.bullets && (React.createElement(IconContainer, { "aria-hidden": true }, icon)),
                    variant === BulletListVariants.numbered && (React.createElement(Number, null, formatPath(currentPath, isRTL))),
                    React.createElement(StyledSpacer, { spacing: Spacings.xSmall, orientation: Orientations.horizontal }),
                    React.createElement(Content, null, child)))));
    })));
});
BulletList.displayName = 'BulletList';
export default BulletList;
const Container = styled.ul `
  display: table;
  border-collapse: collapse;
`;
const Content = styled.div `
  display: table-cell;
`;
const StyledSpacer = styled(Spacer) `
  display: table-cell;
`;
const Number = styled.span(({ theme }) => css `
    display: table-cell;
    color: ${theme.bulletList.variants.numbered.color};
    /* We need to keep the numbers in LTR direction */
    direction: ltr;
  `);
const ListItem = styled.li(({ addSpace, compact }) => css([
    css `
      display: table-row;
    `,
    addSpace &&
        css `
        border-block-start: ${Spacings.large} solid transparent;
        ul li {
          border-top-width: ${createCustomSpacing(20 / 16)};
        }
        ul li ul li {
          border-top-width: ${Spacings.medium};
        }
      `,
    compact &&
        addSpace &&
        css `
        border-block-start: ${Spacings.medium} solid transparent;
        ul li {
          border-top-width: ${Spacings.small};
        }
        ul li ul li {
          border-top-width: ${Spacings.xSmall};
        }
      `,
]));
const IconContainer = styled.div(({ theme }) => css(css `
    display: table-cell;
    width: ${LineHeights.small};
    height: ${LineHeights.small};
    color: ${theme.bulletList.variants.bullets.color};
    text-align: center;
    vertical-align: top;
    position: relative;
    inset-block-start: -1px;
  `));
